<script setup lang="ts">
import FinancingRequestCollateralList from '@/components/financing-requests/FinancingRequestCollateralList.vue'
import FinancingRequestDocumentsList from '@/components/financing-requests/FinancingRequestDocumentsList.vue'
import FinancingRequestGapList from '@/components/financing-requests/FinancingRequestGapList.vue'
import FinancingRequestObjectCard from '@/components/financing-requests/FinancingRequestObjectCard.vue'
import FinancingRequestReviewNote from '@/components/financing-requests/FinancingRequestReviewNote.vue'
import FinancingRequestStatusSteps from '@/components/financing-requests/FinancingRequestStatusSteps.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestField from '@/components/FinestField.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useEnumOptions } from '@/composables/useEnumOptions'
import { useFieldSave } from '@/composables/useFieldSave'
import { useSyncedModel } from '@/composables/useSyncedModel'
import type { ExtendedEnum, PageProps } from '@/types'
import { DateTime } from '@/utils/business-days'
import { userLeftTheField } from '@/utils/field-leave'
import { useFieldStates, type FieldStateMap } from '@/utils/field-state'
import { toDate, toIsoDate } from '@/utils/field-value'
import { primeInputFocus } from '@/utils/prime-input-classes'
import { toVisibleProjectStatus } from '@/utils/project-status'
import ProjectsController from '@controllers/Investors/ProjectsController'
import { Head, router } from '@inertiajs/vue3'
import DatePicker from 'primevue/datepicker'
import { useConfirm } from 'primevue/useconfirm'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FinancingRequestDocumentAreaData = App.Data.Documents.FinancingRequestDocumentAreaData
import CollateralData = App.Data.EstateObject.CollateralData
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import SubmissionGapReportData = App.Data.Project.SubmissionGapReportData
import CollateralType = App.Enums.EstateObject.Loans.CollateralType

interface ProjectFormData {
  id: string
  status: App.Enums.Project.ProjectStatus
  title: string | null
  comment: string | null
  buyer_type: App.Enums.PersonType | null
  buyer_id: string | null
  loan_type: App.Enums.FinancingRequest.LoanType | null
  total_financing_amount: number | null
  equity_contribution: number | null
  interest_term_years: number | null
  repayment_rate: number | null
  financing_due_date: string | null
  estate_object_id: string
}

interface BuyerOption {
  id: string
  name: string
  type: App.Enums.PersonType
}

interface FinancingRequestEnums {
  loanType: App.Enums.FinancingRequest.LoanType[]
  collateralType: ExtendedEnum<CollateralType>[]
}

const props = defineProps<
  PageProps<{
    project: ProjectFormData
    canDelete: boolean
    fieldStates: FieldStateMap
    submissionGapReport: SubmissionGapReportData
    collaterals: CollateralData[]
    buyerOptions: BuyerOption[]
    enums: FinancingRequestEnums

    estateObject: EstateObjectData
    estateObjectCompletion: number
    estateObjectMissingFields: number
    documentAreas: FinancingRequestDocumentAreaData[]
  }>
>()

const { t, locale } = useI18n()
const confirm = useConfirm()
const toEnumOptions = useEnumOptions()

type FormField = Exclude<keyof ProjectFormData, 'id' | 'status' | 'title' | 'estate_object_id'>

const seedForm = (): Pick<ProjectFormData, FormField> => ({
  comment: props.project.comment,
  buyer_type: props.project.buyer_type,
  buyer_id: props.project.buyer_id,
  loan_type: props.project.loan_type,
  total_financing_amount: props.project.total_financing_amount,
  equity_contribution: props.project.equity_contribution,
  interest_term_years: props.project.interest_term_years,
  repayment_rate: props.project.repayment_rate,
  financing_due_date: props.project.financing_due_date,
})

const { model: form, confirm: confirmSaved } = useSyncedModel(seedForm)

const status = computed(() => toVisibleProjectStatus(props.project.status))

const isOnHold = computed(() => props.submissionGapReport.has_blocking_gaps)

const isInReview = computed(() => status.value === 'in-clearing' && !isOnHold.value)

const objectType = computed(() =>
  props.estateObject.estate_object_type ? t(`enums.estateObjectType.${props.estateObject.estate_object_type}`) : null,
)

const title = computed(() => {
  const street = [props.estateObject.street, props.estateObject.house_number].filter(Boolean).join(' ')
  const city = [props.estateObject.zip_code, props.estateObject.city].filter(Boolean).join(' ')

  return [street, city].filter(Boolean).join(', ') || (props.project.title ?? '')
})

const eyebrow = computed(() => [t('eyebrow'), objectType.value].filter(Boolean).join(' · '))

const subtitle = computed(() =>
  [
    props.estateObject.number_of_units ? t('units', { count: props.estateObject.number_of_units }) : null,
    props.estateObject.living_space_size ? t('squareMeters', { size: props.estateObject.living_space_size }) : null,
    form.value.loan_type ? t(`loanType.${form.value.loan_type}`) : null,
  ]
    .filter(Boolean)
    .join(' · '),
)

const save = useFieldSave({
  url: ProjectsController.update(props.project.id).url,
  reloadProps: ['project', 'fieldStates', 'submissionGapReport'],
  onSaved: confirmSaved,
})

/** Sends a field only once it actually differs from what the server holds. */
const commit = (field: FormField): void => {
  if (form.value[field] === (props.project[field] ?? null)) {
    return
  }

  save({ [field]: form.value[field] })
}

const commitBuyer = (buyerId: string | null): void => {
  const buyer = props.buyerOptions.find((option) => option.id === buyerId)

  form.value.buyer_id = buyer?.id ?? null
  form.value.buyer_type = buyer?.type ?? null

  save({ buyer_id: form.value.buyer_id, buyer_type: form.value.buyer_type })
}

const commitFinancingDueDate = (date: Date | null): void => {
  form.value.financing_due_date = toIsoDate(date)

  commit('financing_due_date')
}

const commitComment = (): void => {
  const comment = form.value.comment?.trim() === '' ? null : form.value.comment

  form.value.comment = comment

  commit('comment')
}

const confirmDelete = (): void =>
  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { title: title.value }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => router.delete(ProjectsController.destroy(props.project.id).url),
  })

const options = (values: readonly string[], group: string) =>
  values.map((value) => ({ value, label: t(`${group}.${value}`) }))

const loanTypeOptions = computed(() => options(props.enums.loanType, 'loanType'))

const hasInterestTerm = computed(() => form.value.loan_type !== 'variable')

/** Switching to a variable loan drops a fixed term that was entered before. */
const commitLoanType = (): void => {
  if (!hasInterestTerm.value && form.value.interest_term_years !== null) {
    form.value.interest_term_years = null

    save({ loan_type: form.value.loan_type, interest_term_years: null })

    return
  }

  commit('loan_type')
}

const buyerSelectOptions = computed(() => props.buyerOptions.map(({ id, name }) => ({ value: id, label: name })))

const collateralTypeOptions = computed(() => toEnumOptions(props.enums.collateralType, 'enums.collateralType'))

const minFinancingDueDate = computed(() => DateTime.now().plus({ days: 14 }).toJSDate())

const { presentationOf } = useFieldStates(() => props.fieldStates)

const conditionsCard = ref<HTMLElement | null>(null)

const inputIdPerField: Record<string, string> = {
  buyer_id: 'buyer',
  loan_type: 'loan-type',
  total_financing_amount: 'total-financing-amount',
  equity_contribution: 'equity-contribution',
  interest_term_years: 'interest-term-years',
  repayment_rate: 'repayment-rate',
  financing_due_date: 'financing-due-date',
}

const focusField = (fieldPath: string | null): void => {
  const inputId = fieldPath === null ? undefined : inputIdPerField[fieldPath]
  const input = inputId === undefined ? null : document.getElementById(inputId)

  ;(input ?? conditionsCard.value)?.scrollIntoView({ behavior: 'smooth', block: 'center' })

  input?.focus()
}
</script>

<template>
  <Head :title="title" />

  <div class="mb-4">
    <Link :href="ProjectsController.index()">
      <FinestButton :label="t('back')" icon="pi pi-arrow-left" variant="ghost" size="sm" />
    </Link>
  </div>

  <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div class="min-w-0">
      <p class="section-label">{{ eyebrow }}</p>
      <h1 class="page-title">{{ title }}</h1>
      <p v-if="subtitle" class="text-ink-2 mt-2">{{ subtitle }}</p>
    </div>

    <div v-if="canDelete" class="flex flex-none flex-wrap justify-end gap-2">
      <FinestButton variant="ghost" icon="pi pi-trash" :label="t('delete.action')" @click="confirmDelete" />
    </div>
  </div>

  <div class="flex flex-col gap-6">
    <FinestCard
      :title="t('sections.status')"
      icon="pi pi-check-circle"
      flush
      :class="{ 'border-warning-subtle-active border': isOnHold }">
      <FinancingRequestStatusSteps :status="status" :on-hold="isOnHold" />

      <FinancingRequestGapList :report="submissionGapReport" @complete="focusField" />

      <FinancingRequestReviewNote v-if="isInReview" />
    </FinestCard>

    <div class="grid gap-6 xl:grid-cols-4">
      <div class="flex flex-col gap-6 xl:col-span-3">
        <div ref="conditionsCard">
          <FinestCard :title="t('sections.keyFacts')" icon="pi pi-calculator" flush>
            <div class="grid gap-4 px-5 pb-5 sm:grid-cols-2 lg:grid-cols-3">
              <FinestField :label="t('fields.buyer')" input-id="buyer" :required="presentationOf('buyer_id').required">
                <FinestSelect
                  id="buyer"
                  :model-value="form.buyer_id"
                  :options="buyerSelectOptions"
                  :placeholder="t('selectPlaceholder')"
                  :aria-label="t('fields.buyer')"
                  :state="presentationOf('buyer_id').state"
                  @change="commitBuyer" />
              </FinestField>

              <FinestField
                :label="t('fields.loan_type')"
                input-id="loan-type"
                :required="presentationOf('loan_type').required">
                <FinestSelect
                  id="loan-type"
                  v-model="form.loan_type"
                  :options="loanTypeOptions"
                  :placeholder="t('selectPlaceholder')"
                  :aria-label="t('fields.loan_type')"
                  :state="presentationOf('loan_type').state"
                  @change="commitLoanType" />
              </FinestField>

              <FinestField
                :label="t('fields.total_financing_amount')"
                input-id="total-financing-amount"
                :required="presentationOf('total_financing_amount').required">
                <FinestInputNumber
                  id="total-financing-amount"
                  v-model="form.total_financing_amount"
                  mode="currency"
                  :locale="locale"
                  :aria-label="t('fields.total_financing_amount')"
                  :state="presentationOf('total_financing_amount').state"
                  @blur="userLeftTheField() && commit('total_financing_amount')" />
              </FinestField>

              <FinestField :label="t('fields.equity_contribution')" input-id="equity-contribution">
                <FinestInputNumber
                  id="equity-contribution"
                  v-model="form.equity_contribution"
                  mode="currency"
                  :locale="locale"
                  :aria-label="t('fields.equity_contribution')"
                  @blur="userLeftTheField() && commit('equity_contribution')" />
              </FinestField>

              <FinestField
                v-if="hasInterestTerm"
                :label="t('fields.interest_term_years')"
                input-id="interest-term-years"
                :required="presentationOf('interest_term_years').required">
                <FinestInputNumber
                  id="interest-term-years"
                  v-model="form.interest_term_years"
                  :locale="locale"
                  :suffix="t('yearsSuffix')"
                  :min-fraction-digits="0"
                  :max-fraction-digits="0"
                  :use-grouping="false"
                  :aria-label="t('fields.interest_term_years')"
                  :state="presentationOf('interest_term_years').state"
                  @blur="userLeftTheField() && commit('interest_term_years')" />
              </FinestField>

              <FinestField
                :label="t('fields.repayment_rate')"
                input-id="repayment-rate"
                :required="presentationOf('repayment_rate').required">
                <FinestInputNumber
                  id="repayment-rate"
                  v-model="form.repayment_rate"
                  :locale="locale"
                  suffix=" %"
                  :min-fraction-digits="2"
                  :max-fraction-digits="2"
                  :aria-label="t('fields.repayment_rate')"
                  :state="presentationOf('repayment_rate').state"
                  @blur="userLeftTheField() && commit('repayment_rate')" />
              </FinestField>

              <FinestField :label="t('fields.financing_due_date')" input-id="financing-due-date">
                <DatePicker
                  input-id="financing-due-date"
                  :model-value="toDate(form.financing_due_date)"
                  :min-date="minFinancingDueDate"
                  :aria-label="t('fields.financing_due_date')"
                  :pt="{ pcInputText: { root: { class: primeInputFocus } } }"
                  fluid
                  show-icon
                  icon-display="input"
                  date-format="dd.mm.yy"
                  @update:model-value="commitFinancingDueDate(($event as Date) ?? null)" />
              </FinestField>
            </div>

            <div class="border-line-2 border-t px-5 py-5">
              <FinancingRequestCollateralList
                :project-id="project.id"
                :initial-collaterals="collaterals"
                :collateral-type-options="collateralTypeOptions" />
            </div>
          </FinestCard>
        </div>

        <FinestCard :title="t('notes.title')" icon="pi pi-comment">
          <Textarea
            v-model="form.comment"
            :placeholder="t('notes.placeholder')"
            :aria-label="t('notes.title')"
            :pt="{ root: { class: primeInputFocus } }"
            class="w-full"
            rows="5"
            @blur="userLeftTheField() && commitComment()" />
        </FinestCard>
      </div>

      <div class="flex flex-col gap-6">
        <FinancingRequestObjectCard
          :estate-object="estateObject"
          :project-id="project.id"
          :completion="estateObjectCompletion"
          :missing-fields="estateObjectMissingFields" />

        <FinancingRequestDocumentsList :areas="documentAreas" />
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "eyebrow": "Finanzierungsanfrage",
    "back": "Zu den Anfragen",
    "units": "{count} Einheiten",
    "squareMeters": "{size} m²",
    "yearsSuffix": " Jahre",
    "selectPlaceholder": "Bitte wählen …",
    "sections": {
      "status": "Status",
      "keyFacts": "Eckdaten & Konditionen"
    },
    "fields": {
      "buyer": "Käufer",
      "loan_type": "Gewünschte Darlehensart",
      "total_financing_amount": "Darlehenssumme",
      "equity_contribution": "Eigenkapital",
      "interest_term_years": "Zinsbindung",
      "repayment_rate": "Tilgung",
      "financing_due_date": "Finanzierung soll stehen bis"
    },
    "notes": {
      "title": "Sonstige Notizen",
      "placeholder": "Was sollten wir und die Banken zu dieser Finanzierung wissen?"
    },
    "loanType": {
      "annuity": "Annuitätendarlehen",
      "variable": "Variables Darlehen"
    },
    "delete": {
      "action": "Anfrage löschen",
      "header": "Finanzierungsanfrage löschen",
      "message": "„{title}“ wird gelöscht. Die Objektdaten in deinem Portfolio bleiben erhalten.",
      "reject": "Abbrechen",
      "accept": "Löschen"
    }
  },
  "en": {
    "eyebrow": "Financing request",
    "back": "Back to requests",
    "units": "{count} units",
    "squareMeters": "{size} m²",
    "yearsSuffix": " years",
    "selectPlaceholder": "Please select …",
    "sections": {
      "status": "Status",
      "keyFacts": "Key facts & terms"
    },
    "fields": {
      "buyer": "Buyer",
      "loan_type": "Preferred loan type",
      "total_financing_amount": "Loan amount",
      "equity_contribution": "Equity",
      "interest_term_years": "Interest rate period",
      "repayment_rate": "Repayment rate",
      "financing_due_date": "Financing needed by"
    },
    "notes": {
      "title": "Other notes",
      "placeholder": "What should we and the banks know about this financing?"
    },
    "loanType": {
      "annuity": "Annuity loan",
      "variable": "Variable loan"
    },
    "delete": {
      "action": "Delete request",
      "header": "Delete financing request",
      "message": "“{title}” will be deleted. The property data in your portfolio is kept.",
      "reject": "Cancel",
      "accept": "Delete"
    }
  }
}
</i18n>
